import api from '../utils/api';

/**
 * @typedef {Object} Coupon
 * @property {string} _id
 * @property {string} code
 * @property {number} discount
 * @property {string} discountType - 'percentage', 'fixed'
 * @property {string} expirationDate
 * @property {number} maxUses
 * @property {number} usedCount
 * @property {boolean} isActive
 * @property {string} applicableType - 'all', 'course', 'subscription'
 * @property {string[]} applicableIds
 * @property {('course'|'package'|'cart')} [targetType] - Coupon target type. 'cart' applies at checkout across the cart subtotal.
 * @property {string[]} [applicablePackages] - Package IDs when targetType is 'package' and scope is 'specific'
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * @typedef {Object} CreateCouponData
 * @property {string} code
 * @property {number} discount
 * @property {string} discountType
 * @property {string} expirationDate
 * @property {number} maxUses
 * @property {string} applicableType
 * @property {string[]} [applicableIds]
 * @property {('course'|'package'|'cart')} [targetType] - Coupon target type. 'cart' applies at checkout across the cart subtotal.
 * @property {string[]} [applicablePackages] - Package IDs when targetType is 'package' and scope is 'specific'
 */

export const couponService = {
  // ==================
  // VALIDATION ROUTES
  // ==================

  /**
   * Validate coupon against a course or package purchase.
   * Pass the relevant id; the backend inspects targetType/scope server-side.
   * @param {string} code - Coupon code entered by the user
   * @param {string} [courseId] - Course id when validating against a course purchase
   * @param {string} [packageId] - Package id when validating against a package purchase
   * @returns {Promise<{status: string, isValid: boolean, discountAmount: number, finalPrice: number, coupon: Coupon}>}
   */
  validateCoupon: async (code, courseId, packageId) => {
    const response = await api.post('/api/coupons/validate', { code, courseId, packageId });
    return response;
  },

  // ==================
  // MANAGEMENT ROUTES (ADMIN/INSTRUCTOR)
  // ==================

  /**
   * Create coupon
   * @param {CreateCouponData} couponData 
   * @returns {Promise<{status: string, coupon: Coupon}>}
   */
  createCoupon: async (couponData) => {
    const response = await api.post('/api/coupons', couponData);
    return response;
  },

  /**
   * Get all coupons
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {boolean} params.isActive - Filter by active status
   * @param {string} params.discountType - Filter by discount type
   * @param {string} params.applicableScope - Filter by applicable scope
   * @param {string} params.targetType - Filter by coupon target type ('course' or 'package')
   * @param {string} params.targetType - Filter by coupon target type ('course', 'package', or 'cart')
   * @param {string} params.search - Search by code or description
   * @returns {Promise<{status: string, count: number, total: number, data: Coupon[]}>}
   */
  getAllCoupons: async (params = {}) => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params.discountType) queryParams.append('discountType', params.discountType);
    if (params.applicableScope) queryParams.append('applicableScope', params.applicableScope);
    if (params.search) queryParams.append('search', params.search);
    if (params.targetType) queryParams.append('targetType', params.targetType);

    const queryString = queryParams.toString();
    const url = `/api/coupons${queryString ? `?${queryString}` : ''}`;

    const response = await api.get(url);
    return response;
  },

  /**
   * Get coupon by ID
   * @param {string} couponId 
   * @returns {Promise<{status: string, coupon: Coupon}>}
   */
  getCoupon: async (couponId) => {
    const response = await api.get(`/api/coupons/${couponId}`);
    return response;
  },

  /**
   * Update coupon
   * @param {string} couponId 
   * @param {Object} updates 
   * @returns {Promise<{status: string, coupon: Coupon}>}
   */
  updateCoupon: async (couponId, updates) => {
    const response = await api.put(`/api/coupons/${couponId}`, updates);
    return response;
  },

  /**
   * Delete coupon
   * @param {string} couponId 
   * @returns {Promise<{status: string, message: string}>}
   */
  deleteCoupon: async (couponId) => {
    const response = await api.delete(`/api/coupons/${couponId}`);
    return response;
  },

  /**
   * Toggle coupon status (Activate/Deactivate)
   * @param {string} couponId 
   * @returns {Promise<{status: string, coupon: Coupon}>}
   */
  toggleCouponStatus: async (couponId) => {
    const response = await api.patch(`/api/coupons/${couponId}/toggle-status`);
    return response;
  },

  /**
   * Get coupon statistics
   * @param {string} couponId 
   * @returns {Promise<{status: string, stats: Object}>}
   */
  getCouponStats: async (couponId) => {
    const response = await api.get(`/api/coupons/${couponId}/stats`);
    return response;
  },

  /**
   * Get coupon usage history
   * @param {string} couponId 
   * @returns {Promise<{status: string, history: Object[]}>}
   */
  getCouponUsageHistory: async (couponId) => {
    const response = await api.get(`/api/coupons/${couponId}/usage-history`);
    return response;
  },
};
